import { countBy, sortByAll, take, uniqueBy } from 'lib/array/arrayUtils'

const MAX_EXPERIENCE_CATEGORY_FILTERS = 6

export interface ExperienceCategoryFilter {
  id: number
  label: string
  count: number
}

interface CategorisedExperience {
  categories?: Array<{ id: number; label: string }>
}

export function getExperienceCategoryFilters(
  experiences: ReadonlyArray<CategorisedExperience>,
  limit: number = MAX_EXPERIENCE_CATEGORY_FILTERS,
): Array<ExperienceCategoryFilter> {
  const categories = experiences.flatMap((experience) => experience.categories ?? [])
  const countsById = countBy(categories, (category) => category.id)

  const ranked = uniqueBy(categories, (category) => category.id)
    .map((category) => ({
      id: category.id,
      label: category.label,
      count: countsById.get(category.id) ?? 0,
    }))

  return take(
    sortByAll(ranked, [
      { selector: (category) => category.count, direction: 'desc' },
      { selector: (category) => category.label, direction: 'asc' },
    ]),
    limit,
  )
}

/**
 * The stored id is advisory: results change under a selection, and a filter whose chip is no
 * longer offered narrows the list with nothing on screen left to clear it.
 */
export function resolveEffectiveCategoryId(
  filters: ReadonlyArray<ExperienceCategoryFilter>,
  selectedCategoryId: number | undefined,
): number | undefined {
  return filters.some((filter) => filter.id === selectedCategoryId) ? selectedCategoryId : undefined
}

export function filterExperiencesByCategory<T extends CategorisedExperience>(
  experiences: Array<T>,
  categoryId: number | undefined,
): Array<T> {
  if (categoryId === undefined) return experiences

  return experiences.filter(
    (experience) => experience.categories?.some((category) => category.id === categoryId),
  )
}
